import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../utils/permissionUtils';
import { SettingsManager } from '../utils/settingsManager';
import { logActivity } from '../utils/activityLogger';
import '../styles/song-grid.css';

// Turn a raw filename into a friendly display title (metadata title wins when present).
// Must stay in sync with the player's cleanupFilename so recents (preset names) match.
const filenameToTitle = (filename) => {
  return filename
    .replace(/\.(musicxml|xml)$/i, '')
    .replace(/[_-]/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/\b\w/g, letter => letter.toUpperCase())
    .trim();
};

const SongGrid = ({ hasLoadedSong, onFileLoad, onLocalFileLoad, onRequireSignIn, tapToPractice = false }) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [files, setFiles] = useState([]);
  const [recentNames, setRecentNames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [downloadingFile, setDownloadingFile] = useState(null);
  const [expandedFile, setExpandedFile] = useState(null);
  const [isCollapsed, setIsCollapsed] = useState(hasLoadedSong);
  const [searchQuery, setSearchQuery] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
        const response = await fetch(`${API_BASE}/api/musicxml?operation=list`);
        const data = await response.json();
        if (!cancelled) setFiles(data.files || []);
      } catch (error) {
        console.error('Failed to load song list:', error);
        if (!cancelled) setLoadFailed(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Collapse when a song loads, re-open when the player returns to its empty state
  useEffect(() => {
    setIsCollapsed(hasLoadedSong);
    setExpandedFile(null);
  }, [hasLoadedSong]);

  // Safari (especially on iOS) often restores the page from its cache when
  // the user comes back, so nothing re-runs; refetch recents whenever the
  // page becomes visible again.
  const [visibilityTick, setVisibilityTick] = useState(0);
  useEffect(() => {
    const bump = () => {
      if (document.visibilityState === 'visible') setVisibilityTick(tick => tick + 1);
    };
    document.addEventListener('visibilitychange', bump);
    window.addEventListener('pageshow', bump);
    return () => {
      document.removeEventListener('visibilitychange', bump);
      window.removeEventListener('pageshow', bump);
    };
  }, []);

  // Recently practiced songs float to the top. Preset names are cleaned-up
  // filenames, so they can be matched against filenameToTitle.
  useEffect(() => {
    let cancelled = false;
    if (!user?.id) {
      setRecentNames([]);
      return undefined;
    }
    (async () => {
      try {
        const recent = await SettingsManager.getRecentPresets(user.id);
        if (!cancelled) setRecentNames(recent || []);
      } catch {
        if (!cancelled) setRecentNames([]);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id, hasLoadedSong, visibilityTick]);

  // Unrestricted songs are visible to everyone; restricted ones only to the
  // users they were shared with. Admins are not exempt here on purpose — the
  // song list mirrors what the visibility settings say, and the storage
  // management dialog is where admins see every song regardless.
  const hasFileAccess = (file) => {
    const restricted = file.metadata?.restrictedUsers;
    if (!restricted || !Array.isArray(restricted) || restricted.length === 0) return true;
    if (!user) return false;
    return restricted.includes(user.id);
  };

  const recentRank = (file) => {
    const index = recentNames.indexOf(filenameToTitle(file.filename));
    return index === -1 ? Infinity : index;
  };

  const accessibleFiles = files
    .filter(hasFileAccess)
    .map(file => ({
      ...file,
      displayTitle: file.metadata?.title || filenameToTitle(file.filename),
      isRecent: recentRank(file) !== Infinity
    }))
    .sort((a, b) => {
      const rankA = recentRank(a);
      const rankB = recentRank(b);
      if (rankA !== rankB) return rankA < rankB ? -1 : 1;
      return a.displayTitle.localeCompare(b.displayTitle);
    });

  // Match anywhere in the title or the raw filename, case-insensitively
  const normalizedQuery = searchQuery.trim().toLowerCase();
  const visibleFiles = normalizedQuery
    ? accessibleFiles.filter(file =>
        file.displayTitle.toLowerCase().includes(normalizedQuery) ||
        file.filename.toLowerCase().includes(normalizedQuery))
    : accessibleFiles;

  const canLoadFromDevice = hasPermission(user, 'manage-xml-files');

  const handleCardClick = (file) => {
    if (downloadingFile) return;
    if (tapToPractice) { handlePractice(file); return; } // phones skip the expanded card
    setExpandedFile(prev => (prev === file.filename ? null : file.filename));
  };

  const handlePractice = async (file) => {
    if (downloadingFile) return;
    if (!user) {
      onRequireSignIn();
      return;
    }

    setDownloadingFile(file.filename);
    logActivity('song_card_select', 'cloud', file.filename,
      `User selected song card: ${file.filename}`,
      { fileSize: file.size });
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(
        `${API_BASE}/api/musicxml?operation=download&url=${encodeURIComponent(file.url)}&userId=${encodeURIComponent(user?.id || '')}&userRole=${encodeURIComponent(user?.role || '')}`
      );

      if (!response.ok) {
        if (response.status === 403) {
          const errorData = await response.json();
          throw new Error(errorData.error || t('cloudStorage.accessDenied'));
        }
        throw new Error(t('cloudStorage.failedToDownloadFile'));
      }

      const content = await response.text();
      setExpandedFile(null);
      onFileLoad(content, file.filename);
    } catch (error) {
      console.error('Song download error:', error);
      alert(t('cloudStorage.failedToDownload'));
    } finally {
      setDownloadingFile(null);
    }
  };

  const handleDownloadPdf = (file) => {
    logActivity('song_pdf_download', 'cloud', file.filename,
      `User downloaded official PDF for: ${file.filename}`);
    window.open(file.pdfUrl, '_blank', 'noopener');
  };

  const handleLocalFile = (event) => {
    const file = event.target.files[0];
    if (file) {
      onLocalFileLoad(file);
      event.target.value = '';
    }
  };

  return (
    <div className={`song-grid-section${isCollapsed ? ' collapsed' : ''}`}>
      <div
        className={`song-grid-heading${hasLoadedSong ? ' toggleable' : ''}`}
        onClick={hasLoadedSong ? () => setIsCollapsed(!isCollapsed) : undefined}
        role={hasLoadedSong ? 'button' : undefined}
      >
        <span className="song-grid-heading-icon">🎵</span>
        <span>{t('songGrid.chooseSong')}</span>
        {hasLoadedSong && (
          <span className="song-grid-toggle">{isCollapsed ? '▶' : '▼'}</span>
        )}
      </div>

      {!isCollapsed && (
        <>
          {!loading && !loadFailed && accessibleFiles.length > 0 && (
            <div className="song-grid-search">
              <span className="song-grid-search-icon">🔍</span>
              <input
                type="search"
                className="song-grid-search-input"
                placeholder={t('songGrid.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') setSearchQuery('');
                  e.stopPropagation();
                }}
                aria-label={t('songGrid.searchPlaceholder')}
              />
              {searchQuery && (
                <button
                  type="button"
                  className="song-grid-search-clear"
                  onClick={() => setSearchQuery('')}
                  title={t('songGrid.clearSearch')}
                >
                  ✕
                </button>
              )}
            </div>
          )}

          {loading ? (
            <div className="song-grid-message">{t('songGrid.loading')}</div>
          ) : loadFailed ? (
            <div className="song-grid-message">{t('cloudStorage.failedToLoad')}</div>
          ) : accessibleFiles.length === 0 ? (
            <div className="song-grid-message">{t('songGrid.noSongs')}</div>
          ) : visibleFiles.length === 0 ? (
            <div className="song-grid-message">{t('songGrid.noMatches')}</div>
          ) : (
            <div className="song-grid-scroll">
              <div className="song-grid">
                {visibleFiles.map(file => {
                  const isExpanded = expandedFile === file.filename;
                  const isDownloading = downloadingFile === file.filename;
                  return (
                    <div
                      key={file.filename}
                      className={`song-card${file.isRecent ? ' recent' : ''}${isExpanded ? ' expanded' : ''}${isDownloading ? ' loading' : ''}`}
                      onClick={() => handleCardClick(file)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleCardClick(file);
                        }
                      }}
                      title={file.displayTitle}
                    >
                      <span className="song-card-icon">
                        {isDownloading ? '⏳' : '🎼'}
                        {file.isRecent && <span className="song-card-recent" title={t('songGrid.recent')}>★</span>}
                      </span>
                      <span className="song-card-title">{file.displayTitle}</span>
                      {isExpanded && (
                        <div className="song-card-actions" onClick={(e) => e.stopPropagation()}>
                          <button
                            className="song-card-action practice"
                            onClick={() => handlePractice(file)}
                            disabled={downloadingFile !== null}
                          >
                            ▶ {t('songGrid.practice')}
                          </button>
                          {file.pdfUrl && (
                            <button
                              className="song-card-action pdf"
                              onClick={() => handleDownloadPdf(file)}
                            >
                              📄 {t('songGrid.downloadPdf')}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {canLoadFromDevice && (
            <div className="song-grid-footer">
              <input
                ref={fileInputRef}
                type="file"
                accept=".musicxml,.xml"
                onChange={handleLocalFile}
                style={{ display: 'none' }}
              />
              <button
                className="load-from-device-btn"
                onClick={() => fileInputRef.current?.click()}
                disabled={downloadingFile !== null}
              >
                📱 {t('songGrid.loadFromDevice')}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SongGrid;
